import axios from 'axios';

function resolveBaseURL() {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.trim().length > 0) return envUrl;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('app.passqik.com')) return 'https://pass-am-backend-production.up.railway.app/api';
  return 'http://localhost:5001/api';
}

const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Let the browser set Content-Type (with boundary) for FormData; default application/json would break multipart
    if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // Log API request for debugging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', config.method?.toUpperCase(), config.url, config.params || config.data);
    }
    // Prefer session over local to avoid stale long-lived tokens
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 API: Token added to request');
      }
    } else if (process.env.NODE_ENV === 'development') {
      console.log('⚠️ API: No token found');
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Helper: refresh tokens (session-first), update storage, and return new token
let refreshPromise = null;
const API_BASE_URL = api.defaults?.baseURL || resolveBaseURL();

async function refreshTokens() {
  // If a refresh is already in flight, reuse it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const sessionRefresh = sessionStorage.getItem('refreshToken');
    const localRefresh = localStorage.getItem('refreshToken');
    const usingSession = !!sessionRefresh;
    const refreshToken = sessionRefresh || localRefresh;

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Use plain axios to avoid this interceptor and potential loops
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
    const { token: newToken, refreshToken: newRefreshToken } = response.data?.data || {};

    if (!newToken || !newRefreshToken) {
      throw new Error('Invalid refresh response');
    }

    // Persist tokens in the same storage the refresh token came from
    if (usingSession) {
      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('refreshToken', newRefreshToken);
      // Clear conflicting local tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } else {
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      // Clear conflicting session tokens
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
    }

    return newToken;
  })();

  try {
    const result = await refreshPromise;
    return result;
  } finally {
    // Allow new refresh cycles after completion
    refreshPromise = null;
  }
}

// Response interceptor: attempt refresh on 401, then retry once
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', response.status, response.config.url, response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || '';

    console.error('❌ API Response Error:', {
      status,
      url,
      message: error.message,
      data: error.response?.data
    });

    // Guard: do not try to refresh for refresh endpoint itself
    const isRefreshCall = url.includes('/auth/refresh');

    if (status === 401 && !originalRequest._retry && !isRefreshCall) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshTokens();
        // Update the original request with the new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔒 API: Token refresh failed, redirecting to login');
        }
        // Clear tokens and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (userData) => api.put('/users/me', userData)
};

// Session API
export const sessionAPI = {
  // Get all sessions
  getSessions: (params = {}) => api.get('/sessions', { params }),
  
  // Get session by ID
  getSession: (id) => api.get(`/sessions/${id}`),
  
  // Create new session
  createSession: (sessionData) => api.post('/sessions', sessionData),
  
  // Update session
  updateSession: (id, sessionData) => api.put(`/sessions/${id}`, sessionData),
  
  // Delete session
  deleteSession: (id) => api.delete(`/sessions/${id}`),
  
  // Get user's sessions
  getUserSessions: () => api.get('/sessions/user'),
  
  // Get sessions by subject
  getSessionsBySubject: (subject) => api.get(`/sessions/subject/${subject}`),
  
  // Join a session and receive meeting access details
  joinSession: (id) => api.post(`/sessions/${id}/join`),
  // Join by code
  joinByCode: (code) => api.post(`/sessions/join-by-code/${code}`),
  // Create a join code (teachers/admin)
  createJoinCode: (id, options = { expiresInMinutes: 60, singleUse: false }) =>
    api.post(`/sessions/${id}/join-code`, options),
  
  // End a session (teachers only)
  endSession: (id) => api.post(`/sessions/${id}/end`),
  
  // Get session recording
  getSessionRecording: (id) => api.get(`/sessions/${id}/recording`),
  
  // Enroll in a session
  enrollInSession: (id) => api.post(`/sessions/${id}/enroll`),
  
  // Get upcoming sessions
  getUpcomingSessions: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return api.get(`/sessions/upcoming?${params}`);
  },
  
  // Get teacher's sessions
  getTeacherSessions: (teacherId, status = null) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/sessions/teacher/${teacherId}${params}`);
  },
  getHistory: (params = {}) => api.get('/sessions/history', { params }),
  answerQnA: (sessionId, qnaId, answer) => api.put(`/sessions/${sessionId}/qna/${qnaId}/answer`, { answer })
  , addAnnouncement: (sessionId, payload) => api.post(`/sessions/${sessionId}/announcements`, payload)
};

// Test API
export const testAPI = {
  // Get all tests with filtering
  getTests: (params = {}) => api.get('/tests', { params }),
  
  // Get test by ID
  getTest: (id) => api.get(`/tests/${id}`),
  
  // Get test questions
  getTestQuestions: (id) => api.get(`/tests/${id}/questions`),
  
  // Get test statistics
  getTestStats: (id) => api.get(`/tests/${id}/stats`),
  
  // Get test attempts
  getTestAttempts: (id, params = {}) => api.get(`/tests/${id}/attempts`, { params }),
  
  // Get test analytics
  getTestAnalytics: (testId, submissionId) => api.get(`/tests/${testId}/analytics/${submissionId}`),
  
  // Get test progress
  getTestProgress: (id) => api.get(`/tests/${id}/progress`),
  
  // Save test progress
  saveTestProgress: (id, progressData) => api.post(`/tests/${id}/progress`, progressData),
  
  // Start test
  startTest: (id) => api.post(`/tests/${id}/start`),
  
  // Answer question
  answerQuestion: (id, questionIndex, selectedOption, timeSpent) => 
    api.post(`/tests/${id}/answer`, { questionIndex, selectedOption, timeSpent }),
  
  // Submit test (long timeout while backend generates Expert Intelligence insight)
  submitTest: (id, submissionData) => api.post(`/tests/${id}/submit`, submissionData, { timeout: 120000 }),
  
  // Complete test
  completeTest: (id) => api.post(`/tests/${id}/complete`),
  
  // Get test results
  getTestResults: (testId, submissionId = null) => {
    if (submissionId) {
      return api.get(`/tests/${testId}/results/${submissionId}`);
    }
    return api.get(`/tests/${testId}/results`);
  },
  
  // Get user's test results (list with full details including aiInsights)
  getUserTestResults: (params = {}) => api.get('/tests/results/user', { params }),

  // Get a single test result by submission id (full details including aiInsights; for practice or regular)
  getTestResultById: (submissionId) => api.get(`/tests/results/single/${submissionId}`),
  
  // Create new test
  createTest: (testData) => api.post('/tests', testData),
  
  // Update test
  updateTest: (id, testData) => api.put(`/tests/${id}`, testData),
  
  // Delete test
  deleteTest: (id) => api.delete(`/tests/${id}`),

  // Get all tests for configuration management
  getTestsForConfig: () => api.get('/tests/config/all'),

  // Update test configuration
  updateTestConfig: (id, config) => api.put(`/tests/${id}/config`, { config }),
  
  // Generate AI questions
  generateQuestions: (params) => api.post('/tests/generate/adaptive', params),
  
  // Get test recommendations
  getRecommendations: () => api.get('/tests/recommendations/for-me'),
  
  // Get syllabus options
  getSyllabusOptions: () => api.get('/tests/syllabus/options'),
  
  // Get practice test options (subject/exam type combinations)
  getPracticeOptions: () => api.get('/tests/practice-options'),
  
  // Generate dynamic practice test
  generatePracticeTest: (params) => api.post('/tests/generate-practice', params),

  // Submit practice test (returns submissionId, aiInsights, score, etc.). Long timeout for Expert Intelligence insight generation.
  submitPracticeTest: (data) => api.post('/tests/practice/submit', data, { timeout: 120000 }),

  // Get in-progress practice test (optional subject, examType; if omitted returns most recent)
  getPracticeProgress: (params = {}) => api.get('/tests/practice/progress', { params }),
  // Save in-progress practice test
  savePracticeProgress: (data) => api.post('/tests/practice/progress', data),

  // Comprehensive insights endpoints
  getComprehensiveInsights: () => api.get('/tests/comprehensive-insights'),
  generateComprehensiveInsights: (data) => api.post('/tests/comprehensive-insights', data)
};

// Exam settings (by exam type + class + subject from question bank)
export const examSettingsAPI = {
  list: () => api.get('/exam-settings'),
  getOne: (params) => api.get('/exam-settings/one', { params }),
  save: (payload) => api.put('/exam-settings', payload)
};

// User API
export const userAPI = {
  // Get current user profile
  getProfile: () => api.get('/users/me'),
  
  // Update current user profile
  updateProfile: (userData) => api.put('/users/me', userData),

  // Upload avatar (profile image)
  uploadAvatar: (formData) =>
    api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Change password
  changePassword: (data) => api.put('/users/me/password', data),

  // Update preferences/settings (maps to backend 'settings')
  updatePreferences: (preferences) => api.put('/users/me', { settings: preferences }),
  
  // Get all users (admin only)
  getUsers: (params = {}) => api.get('/users', { params }),
  
  // Get user by ID
  getUser: (id) => api.get(`/users/${id}`),
  
  // Update user
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  
  // Delete user
  deleteUser: (id) => api.delete(`/users/${id}`),
  
  // Get user statistics
  getUserStats: (id) => api.get(`/users/${id}/stats`),
  
  // Update user role (admin only)
  updateUserRole: (id, role) => api.put(`/users/${id}/role`, { role })
};

// Students API
export const studentsAPI = {
  // Get all students
  getStudents: (params = {}) => api.get('/students', { params }),
  
  // Get student by ID
  getStudent: (id) => api.get(`/students/${id}`),
  
  // Get student by ID (alias for consistency with component usage)
  getStudentById: (id) => api.get(`/students/${id}`),
  
  // Create new student
  createStudent: (studentData) => api.post('/students', studentData),
  
  // Update student
  updateStudent: (id, data) => api.put(`/students/${id}`, data),
  
  // Delete student
  deleteStudent: (id) => api.delete(`/students/${id}`),
  
  // Get student performance
  getStudentPerformance: (id) => api.get(`/students/${id}/performance`),
  
  // Get students statistics
  getStudentsStats: () => api.get('/students/stats/overview'),
  
  // Search students
  searchStudents: (query) => api.get('/students', { params: { q: query } }),
  
  // Get students by grade
  getStudentsByGrade: (grade) => api.get('/students', { params: { grade } }),
  
  // Get students by school
  getStudentsBySchool: (school) => api.get('/students', { params: { school } }),
  
  // Get students by exam type
  getStudentsByExamType: (examType) => api.get('/students', { params: { examType } }),
  
  // Export students data
  exportStudents: (format = 'csv') => api.get('/students/export', { 
    params: { format },
    responseType: 'blob'
  }),
  
  // Bulk operations
  bulkUpdateStudents: (studentIds, updates) => api.put('/students/bulk', { studentIds, updates }),
  bulkDeleteStudents: (studentIds) => api.delete('/students/bulk', { data: { studentIds } }),
  
  // Dashboard stats
  getDashboardStats: () => api.get('/students/dashboard/stats'),
  
  // Get student progress over time
  getStudentProgress: (id, timeframe = 'month') => api.get(`/students/${id}/progress`, { 
    params: { timeframe } 
  }),
  
  // Get student test results
  getStudentTestResults: (id, params = {}) => api.get(`/students/${id}/test-results`, { params }),
  
  // Get student session history
  getStudentSessionHistory: (id, params = {}) => api.get(`/students/${id}/sessions`, { params }),
  // Assign student to teacher (teacherId optional; defaults to current teacher)
  assignToTeacher: (studentId, teacherId = null) => api.put(`/students/${studentId}/assign-teacher`, teacherId ? { teacherId } : {}),
  // Unassign student from current teacher
  unassignTeacher: (studentId) => api.delete(`/students/${studentId}/assign-teacher`)
};

// Dashboard API
export const dashboardAPI = {
  // Get dashboard data based on user role
  getDashboardData: (params = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🌐 API: getDashboardData called with params:', params);
    }
    const { role } = params;
    
    let endpoint, requestParams;
    if (role === 'admin') {
      endpoint = '/admin/dashboard';
      requestParams = undefined;
      if (process.env.NODE_ENV === 'development') {
        console.log('🔑 API: Admin role detected, calling:', endpoint);
      }
    } else {
      endpoint = '/analytics/dashboard';
      requestParams = { params };
      if (process.env.NODE_ENV === 'development') {
        console.log('👤 API: User role detected, calling:', endpoint, 'with params:', requestParams);
      }
    }
    
    return api.get(endpoint, requestParams);
  },
  getStats: () => api.get('/analytics/dashboard'),
  getRecentActivity: (limit = 10) => api.get('/analytics/recent-activity', { params: { limit } }),
  getUpcomingEvents: () => api.get('/sessions/upcoming'),
  getQuickActions: () => api.get('/analytics/quick-actions'),
  getProgressSummary: () => api.get('/analytics/progress'),
  getAnnouncements: () => api.get('/admin/announcements'),
  markAnnouncementRead: (announcementId) => api.put(`/admin/announcements/${announcementId}/read`)
};

// Question API
export const questionAPI = {
  getAllQuestions: (params = {}) => api.get('/questions', { params }),
  getQuestionById: (questionId) => api.get(`/questions/${questionId}`),
  createQuestion: (questionData) => api.post('/questions', questionData),
  updateQuestion: (questionId, questionData) => api.put(`/questions/${questionId}`, questionData),
  updateQuestionStatus: (questionId, status) => api.patch(`/questions/${questionId}/status`, { status }),
  deleteQuestion: (questionId) => api.delete(`/questions/${questionId}`),
  getQuestionsByCategory: (category, params = {}) => api.get(`/questions/category/${category}`, { params }),
  getQuestionsByDifficulty: (difficulty, params = {}) => api.get(`/questions/difficulty/${difficulty}`, { params }),
  searchQuestions: (query, params = {}) => api.get('/questions/search', { params: { q: query, ...params } }),
  getQuestionStats: (questionId) => api.get(`/questions/${questionId}/stats`),
  reportQuestion: (questionId, reason) => api.post(`/questions/${questionId}/report`, { reason }),
  bookmarkQuestion: (questionId) => api.post(`/questions/${questionId}/bookmark`),
  unbookmarkQuestion: (questionId) => api.delete(`/questions/${questionId}/bookmark`),
  rateQuestion: (questionId, rating) => api.post(`/questions/${questionId}/rate`, { rating }),
  getQuestionComments: (questionId) => api.get(`/questions/${questionId}/comments`),
  addQuestionComment: (questionId, comment) => api.post(`/questions/${questionId}/comments`, { comment }),
  getFilterOptions: () => api.get('/questions/filter-options')
};

// Analytics API
export const analyticsAPI = {
  // Get dashboard analytics
  getDashboardStats: () => api.get('/analytics/dashboard'),
  
  // Get user performance
  getUserPerformance: (userId) => api.get(`/analytics/user/${userId}`),
  
  // Get performance analytics with filters
  getPerformanceAnalytics: (filters = {}) => api.get('/analytics/performance', { params: filters }),
  
  // Get progress analytics (by userId, for parent/teacher)
  getProgressAnalytics: (userId, params = {}) => api.get(`/analytics/progress/${userId}`, { params }),
  // Get progress data for Progress page: from past (completed) tests; all students for teacher/admin, own for student
  getProgressData: (params = {}) => api.get('/analytics/progress', { params }),
  
  // Get weak areas analysis
  getWeakAreas: (userId) => api.get(`/analytics/weak-areas/${userId}`),
  
  // Get session analytics
  getSessionAnalytics: (sessionId) => api.get(`/analytics/session/${sessionId}`),
  
  // Get subject performance
  getSubjectPerformance: (subject) => api.get(`/analytics/subject/${subject}`)
};

export const childrenAPI = {
  getProgress: (params = {}) => api.get('/children/progress', { params }),
  getResults: (params = {}) => api.get('/children/results', { params }),
  getSessions: (params = {}) => api.get('/children/sessions', { params })
};

export const insightsAPI = {
  getGuardianInsights: () => api.get('/analytics/insights')
};

export const subjectsAPI = {
  getSubjectNames: () => api.get('/subjects/names'),
  /** Options from Subject settings: { examTypes, classes, subjects: { [examType]: [name] }, subjectsFull } */
  getOptions: () => api.get('/subjects/options')
};

// Public config (no auth) - app name etc. Use full URL so path is correct and no auth header is sent.
export const getPublicConfig = () =>
  axios.get(`${API_BASE_URL}/config`).then((r) => r.data?.data || r.data || {});

export const communicationsAPI = {
  getMessages: (params = {}) => api.get('/messages', { params }),
  getMeetings: (params = {}) => api.get('/meetings', { params }),
  getMeeting: (id) => api.get(`/meetings/${id}`),
  updateMeetingStatus: (id, status) => api.patch(`/meetings/${id}/status`, { status }),
  getPossibleInvitees: (type) => api.get('meetings/possible-invitees', { params: { type } }),
  createMeeting: (data) => api.post('/meetings', data),
  joinMeeting: (id) => api.get(`/meetings/${id}/join`),
  getNotifications: (params = {}) => api.get('/notifications', { params }),
  sendMessageToTeacher: (payload) => api.post('/messages/send', payload)
};

export const vtpassAPI = {
  purchaseAirtime: (phone, amount, network = null) =>
    api.post('/vtpass/airtime', {
      phone: String(phone).trim(),
      amount: parseInt(amount, 10),
      ...(network ? { network: String(network).trim().toLowerCase() } : {})
    })
};

// File upload API
export const fileAPI = {
  // Upload file
  uploadFile: (file, type = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    return api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Delete file
  deleteFile: (fileId) => api.delete(`/files/${fileId}`),
  
  // Get file info
  getFile: (fileId) => api.get(`/files/${fileId}`)
};

// Admin API
export const adminAPI = {
  // Dashboard stats
  getDashboardStats: () => api.get('/admin/dashboard'),
  
  // User management
  getAllUsers: (params = {}) => api.get('/admin/users', { params }),
  // Get aggregated user stats (supports filtering by role)
  getUsersStats: (params = {}) => api.get('/admin/users/stats', { params }),
  updateUserStatus: (userId, status) => api.patch(`/admin/users/${userId}/status`, { status }),
  getUserById: (userId) => api.get(`/admin/users/${userId}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  resetUserPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  bulkUpdateUsers: (userIds, payload) => api.patch('/admin/users/bulk-status', { ids: userIds, status: payload.status }),
  bulkDeleteUsers: (userIds) => api.delete('/admin/users/bulk', { data: { ids: userIds } }),
  banUser: (userId) => api.post(`/admin/users/${userId}/ban`),
  unbanUser: (userId) => api.post(`/admin/users/${userId}/unban`),
  
  // Content management
  getAllQuestions: (params = {}) => api.get('/admin/questions', { params }),
  updateQuestion: (questionId, questionData) => api.put(`/admin/questions/${questionId}`, questionData),
  deleteQuestion: (questionId) => api.delete(`/admin/questions/${questionId}`),
  getAllTests: (params = {}) => api.get('/admin/tests', { params }),
  updateTest: (testId, testData) => api.put(`/admin/tests/${testId}`, testData),
  deleteTest: (testId) => api.delete(`/admin/tests/${testId}`),
  getAllSessions: (params = {}) => api.get('/admin/sessions', { params }),
  updateSession: (sessionId, sessionData) => api.put(`/admin/sessions/${sessionId}`, sessionData),
  deleteSession: (sessionId) => api.delete(`/admin/sessions/${sessionId}`),
  // Manual recharge
  rechargePhone: (payload) => api.post('/admin/airtime/recharge', payload),
  
  // System settings
  getSystemSettings: () => api.get('/admin/settings'),
  updateSystemSettings: (settings) => api.put('/admin/settings', settings),
  resetSystemSettings: () => api.post('/admin/settings/reset'),
  testConnection: (type, options = {}) => {
    const body = { type, ...(options.openaiApiKey != null && { openaiApiKey: options.openaiApiKey }) };
    return api.post('/admin/test-connection', body, { headers: options.headers || {} });
  },
  // Subject management (settings)
  getSubjects: (params = {}) => api.get('/admin/subjects', { params }),
  createSubject: (data) => api.post('/admin/subjects', data),
  updateSubject: (id, data) => api.put(`/admin/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/admin/subjects/${id}`),
  getSystemLogs: (params = {}) => api.get('/admin/logs', { params }),
  clearSystemLogs: () => api.delete('/admin/logs'),
  
  // Analytics
  getAnalytics: (params = {}) => api.get('/admin/analytics', { params }),
  exportData: (type, params = {}) => api.get(`/admin/export/${type}`, { params, responseType: 'blob' }),
  
  // Syllabus management
  getSyllabi: (params = {}) => api.get('/syllabus', { params }),
  getSyllabusById: (id) => api.get(`/syllabus/${id}`),
  uploadSyllabus: (formData) => api.post('/syllabus/upload', formData, {
    headers: { 'Content-Type': undefined }
  }),
  processSyllabus: (id, options = {}) => {
    // Dev-only: allow passing OpenAI key via header to backend when explicitly enabled
    const allowOverride = process.env.NODE_ENV === 'development' && String(process.env.REACT_APP_ALLOW_OPENAI_KEY_OVERRIDE).toLowerCase() === 'true';
    const devKey = allowOverride ? (process.env.REACT_APP_OPENAI_API_KEY || localStorage.getItem('OPENAI_API_KEY')) : null;

    const headers = {
      ...(options.headers || {}),
      ...(devKey ? { 'x-openai-api-key': devKey } : {})
    };

    const params = options.retry ? { retry: 'true' } : {};
    return api.post(`/syllabus/${id}/process`, {}, { headers, params });
  },
  downloadSyllabus: (id) => api.get(`/syllabus/${id}/download`, { responseType: 'blob' }),
  updateSyllabus: (id, data) => api.put(`/syllabus/${id}`, data),
  deleteSyllabus: (id) => api.delete(`/syllabus/${id}`),
  getSyllabusStats: () => api.get('/syllabus/stats/overview'),

  // Past Questions (upload past exam PDFs linked to processed syllabi for AI style learning)
  getPastQuestions: (params = {}) => api.get('/past-questions', { params }),
  getProcessedSyllabiForPastQuestions: (params = {}) => api.get('/past-questions/syllabi', { params }),
  uploadPastQuestion: (formData) => api.post('/past-questions', formData, {
    headers: { 'Content-Type': undefined }
  }),
  deletePastQuestion: (id) => api.delete(`/past-questions/${id}`),
  triggerPastQuestionExtraction: (id) => api.post(`/past-questions/${id}/extract`),

  // AI Question Generation from Syllabi
  generateQuestionsFromSyllabus: (syllabusId, options = {}) => api.post(`/syllabus/${syllabusId}/generate-questions`, options, {
    timeout: 120000 // 2 minutes timeout for question generation
  }),
  saveGeneratedQuestions: (data) => api.post('/questions/save-generated', data)
  ,
  // VTPass settings management
  getVTPassSettings: () => api.get('/vtpass/admin/settings'),
  updateVTPassSettings: (payload) => api.put('/vtpass/admin/settings', payload),
  setVTPassMode: (mode) => api.put('/vtpass/admin/mode', { mode })
  ,
  // Airtime transactions logs
  getAirtimeTransactions: (params = {}) => api.get('/admin/airtime/transactions', { params })
  ,
  // Rewards management (admin)
  getRewardHistory: (params = {}) => api.get('/admin/rewards/history', { params }),
  retryReward: (rewardId, phoneNumber = null) => api.post('/admin/rewards/retry', { rewardId, phoneNumber }),
  exportRewardHistory: (params = {}) => api.get('/admin/rewards/history/export', { params }),
  // Reward settings (admin alias endpoints)
  getRewardSettings: () => api.get('/admin/rewards/settings'),
  updateRewardSettings: (settings) => {
    // Transform UI settings to backend ReferralSettings shape
    const payload = {
      registrationRewards: {
        newUserBonus: settings.registrationBonus,
        referrerBonus: settings.referralBonus
      },
      payoutSettings: {
        autoPayoutEnabled: !!settings.autoRewardEnabled
      }
      // Note: minimumReferrals is not currently supported by backend settings
    };
    return api.put('/admin/rewards/settings', payload);
  }
};

// Referral API
export const referralAPI = {
  getStats: () => api.get('/referral-dashboard/stats'),
  generateCode: () => api.post('/referral-dashboard/generate-code'),
  // Generate code for any authenticated user (non-referral role)
  generateUserCode: () => api.post('/referrals/generate-code'),
  // Get current user's referral stats (available to all authenticated users)
  getMyStats: () => api.get('/referrals/my-stats'),
  getLeaderboard: () => api.get('/referral-dashboard/leaderboard'),
  getAnalytics: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/referral-dashboard/analytics?${queryString}`);
  },
  getQRCode: () => api.get('/referrals/qr-code'),
  getRewardHistory: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/referrals/rewards?${queryString}`);
  },
  // Validate referral code and get referrer details
  validateCode: (code) => api.get(`/referrals/validate-code?code=${encodeURIComponent(code)}`),
  processReward: (rewardId) => api.post(`/referrals/rewards/${rewardId}/process`),
  // Admin referrals endpoints
  getAdminStats: () => api.get('/admin/referrals/stats'),
  getAdminUsers: (params = {}) => api.get('/admin/referrals/users', { params }),
  getAdminQRCode: (params = {}) => api.get('/admin/referrals/qr', { params }),
  sendReward: (payload) => api.post('/referrals/admin/send-reward', payload)
}

export { api };
export default api;
